import type { FormValues, FormValuesEvent } from "@/widgets/classic-form/form";
import { createEventDefaultInstance } from "@/widgets/classic-form/form";
import { getEventModelName } from "@/entities/data-model";
import type { HierarchicalFieldGroup } from "@/entities/form-metadata";
import { LOCATION_EXACTNESS_SCOPE } from "@/entities/place";

const populateInstances = ({
    formValuesEventsData,
    eventKey,
    dataModelEvent,
    matchAllTermsExactly,
    isPlaceExactnessEnabled,
    isEditForm,
    defaultYearProximity,
}: {
    formValuesEventsData: FormValues["events"];
    eventKey: string;
    dataModelEvent?: FormValuesEvent;
    matchAllTermsExactly: boolean;
    isPlaceExactnessEnabled: boolean;
    isEditForm: boolean;
    defaultYearProximity: number | null;
}) => {
    if (formValuesEventsData[eventKey]?.instances.length) return;
    if (dataModelEvent && dataModelEvent.instances.length) {
        const instances =
            matchAllTermsExactly && isPlaceExactnessEnabled && !isEditForm
                ? dataModelEvent.instances.map((instance) => ({
                      ...instance,
                      dateProximity: { year: 0, month: 0, day: 0 },
                      locationExactness: LOCATION_EXACTNESS_SCOPE.ThisPlace,
                  }))
                : dataModelEvent.instances.map((instance) => {
                      // Case 7: URL-derived instance with no year gets the default proximity at birth
                      if (defaultYearProximity !== null && instance.date.year == null) {
                          return {
                              ...instance,
                              dateProximity: { year: defaultYearProximity, month: 0, day: 0 },
                          };
                      }
                      return { ...instance };
                  });
        formValuesEventsData[eventKey]?.instances.push(...instances);
    } else if (formValuesEventsData[eventKey]) {
        const baseInstance = createEventDefaultInstance(defaultYearProximity);
        const instance =
            matchAllTermsExactly && isPlaceExactnessEnabled && !isEditForm
                ? {
                      ...baseInstance,
                      dateProximity: { year: 0, month: 0, day: 0 },
                      locationExactness: LOCATION_EXACTNESS_SCOPE.ThisPlace,
                  }
                : baseInstance;
        formValuesEventsData[eventKey].instances.push(instance);
    }
};

export const populateHierarchicalFields = ({
    fieldGroup,
    dataModel,
    formValuesEventsData,
    matchAllTermsExactly,
    isEditForm,
    defaultYearProximity = null,
}: {
    fieldGroup: HierarchicalFieldGroup;
    dataModel: FormValues;
    formValuesEventsData: FormValues["events"];
    matchAllTermsExactly: boolean;
    isEditForm?: boolean;
    defaultYearProximity?: number | null;
}) => {
    const { events: eventsFromDataModel } = dataModel;
    fieldGroup.hierarchies.forEach((hierarchy) => {
        const dynamicEventKey = getEventModelName({
            eventType: hierarchy.eventType,
            relation: hierarchy.relation,
        });
        if (!dynamicEventKey) return;

        // Enabled when every field allows exactness. An empty fields array yields true.
        const isPlaceExactnessEnabled = hierarchy.fields.every((field) => field.features.showExactness !== false);

        const dataModelEvent = eventsFromDataModel[dynamicEventKey];
        if (!formValuesEventsData[dynamicEventKey]) {
            formValuesEventsData[dynamicEventKey] = {
                eventName: hierarchy.eventType as string,
                instances: [],
                relation: hierarchy.relation,
            };
        }
        populateInstances({
            formValuesEventsData,
            eventKey: dynamicEventKey,
            dataModelEvent,
            matchAllTermsExactly,
            isPlaceExactnessEnabled,
            isEditForm: isEditForm ?? false,
            defaultYearProximity: defaultYearProximity ?? null,
        });
    });
};
